import { ConfigurationService } from '@/common/services/configuration/configuration.service';
import { CurrentReservationService } from '@/common/services/reservation/current';
import { INPUT_DATE } from '@/common/utils/date/formats';
import { isValid } from '@/common/utils/validation/common.utils';
import { PersonalInformation } from '@/core/domain/reservation/models/personal-information.model';
import { GetReservationParams } from '@/core/domain/reservation/models/reservation-response';
import { ReservationRepository } from '@/core/domain/reservation/repositories/reservation.respository';
import {
    clearReservation,
    saveReservation,
    updateReservation,
} from '@/core/reservation/store/reservation.actions';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { environment } from '@/env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { first, from, Observable, of, switchMap, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ReservationImplRepository extends ReservationRepository {
    constructor(
        private readonly store: Store,
        private readonly reservation: CurrentReservationService,
        private readonly http: HttpClient,
        private readonly configuration: ConfigurationService,
    ) {
        super();
    }

    override doReservation(
        personalInformation: PersonalInformation,
    ): Observable<any> {
        return this.configuration.requestFeatureFlags().pipe(
            switchMap((item) => {
                this.configuration.featuresFlags.next(item);
                if (item.reservation !== true) {
                    return throwError(() => {
                        return {
                            error: {
                                type: 'ERROR',
                                action: 'CANCEL',
                                code: 'RESERVATIONS_DISABLED',
                                diagnosticInformation: '',
                                parameters: [],
                            },
                        };
                    });
                }
                return this.http.post(
                    [
                        environment.alamoApi.BASE_HOST,
                        environment.alamoApi.ENDPOINTS.doReservation,
                    ].join('/'),
                    personalInformation,
                );
            }),
        );
    }

    override getReservation(params: GetReservationParams): Observable<any> {
        const url = [
            environment.alamoApi.BASE_HOST,
            environment.alamoApi.ENDPOINTS.reservationPreCheckIn,
            params.userId,
        ].join('/');
        const headers = new HttpHeaders({
            token: params.token,
        });
        return this.http.get(url, { headers });
    }

    override updateReservation(reservation: any): Observable<boolean> {
        this.store.dispatch(
            updateReservation({
                value: reservation,
            }),
        );
        return of(true);
    }

    //FIX: agregar interface
    private recoveryCurrentHour(reservation: any): void {
        this.reservation.current.startHour = reservation.startHour;
        this.reservation.current.endHour = reservation.endHour;
    }

    private recoveryCurrentDate(reservation: any): void {
        this.reservation.current.startDate = DateTime.fromISO(
            reservation.startDate,
        ).toFormat(INPUT_DATE);
        this.reservation.current.endDate = DateTime.fromISO(
            reservation.endDate,
        ).toFormat(INPUT_DATE);
    }

    //FIX: agregar interface
    private recoveryLocations(reservation: any): void {
        this.reservation.locationName = _.get(reservation, 'location.name', '');
        this.reservation.differentLocationName = _.get(
            reservation,
            'differentLocation.name',
            '',
        );
        if (isValid(reservation.locationSelected)) {
            this.reservation.current.location = [reservation.locationSelected];
        }
        if (isValid(reservation.differentLocationSelected)) {
            this.reservation.current.differentLocation = [
                reservation.differentLocationSelected,
            ];
            this.recoveryDifferentLocationItem(reservation);
        } else {
            this.reservation.current.differentLocation = [];
            this.reservation.differentLocations = [];
        }
        this.reservation.showDifferenteLocation = isValid(
            reservation.differentLocationSelected,
        );
    }

    private recoveryDifferentLocationItem(reservation: any): void {
        const isListed = _.some(
            this.reservation.differentLocations,
            (item: any) => item?.id === reservation.differentLocationSelected,
        );
        if (isListed) {
            return;
        }
        const selected = _.get(reservation, 'differentLocation');
        if (isValid(selected)) {
            this.reservation.differentLocations = [selected];
        }
    }

    private recoveryVehicle(reservation: any): void {
        this.reservation.vehicle = _.get(reservation, 'vehicle', {});
    }

    private recoveryInsurances(reservation: any): void {
        this.reservation.insurances = _.get(reservation, 'insurances', []);
    }

    private recoveryProducts(reservation: any): void {
        this.reservation.products = _.get(reservation, 'products', []);
    }

    private recoveryExtraData(reservation: any): void {
        this.reservation.current.age = _.get(reservation, 'age');
        this.reservation.current.sessionId = _.get(reservation, 'uuid');
        this.reservation.current.origin = _.get(reservation, 'origin');
        this.reservation.current.flowType = _.get(reservation, 'flowType');
        this.reservation.current.currency = _.get(reservation, 'currency');
        this.reservation.current.cityInternational = _.get(
            reservation,
            'cityInternational',
        );
    }

    override restoreReservation(): Observable<any> {
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservation) => {
                return from(
                    new Promise((resolve) => {
                        setTimeout(() => {
                            this.recoveryCurrentHour(reservation);
                            this.recoveryLocations(reservation);
                            this.recoveryVehicle(reservation);
                            this.recoveryInsurances(reservation);
                            this.recoveryProducts(reservation);
                            this.recoveryCurrentDate(reservation);
                            this.recoveryExtraData(reservation);
                            resolve(reservation);
                        }, 0);
                    }),
                );
            }),
        );
    }

    override clearReservation(): Observable<boolean> {
        this.store.dispatch(clearReservation());
        return of(true);
    }

    override saveReservation(): Observable<true> {
        this.store.dispatch(
            saveReservation({
                value: {
                    startDate: this.reservation.startDatetime,
                    endDate: this.reservation.endDatetime,
                    startHour: this.reservation.current.startHour,
                    endHour: this.reservation.current.endHour,
                    discountCode: this.reservation.current.discountCode,
                    location: _.find(
                        this.reservation.locations,
                        (item: any) =>
                            item.id ===
                            _.first(this.reservation.current.location),
                    ),
                    locationSelected: _.first(
                        this.reservation.current.location,
                    ),
                    differentLocation: _.find(
                        this.reservation.differentLocations,
                        (item: any) =>
                            item.id ===
                            _.first(this.reservation.current.differentLocation),
                    ),
                    differentLocationSelected: _.first(
                        this.reservation.current.differentLocation,
                    ),
                    age: this.reservation.current.age,
                    origin: this.reservation.current.origin,
                },
            }),
        );
        return of(true);
    }
}
